import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Alert, Typography, Box
} from '@mui/material';
import { shortAbsenceService } from '../../services/api';
import { handleApiError } from '../Common/ApiErrorHandler';

export default function ShortAbsenceRequestDialog({
  open, onClose, onCreated, initialSlot, currentStats
}) {
  const [form, setForm] = useState({
    date: '',
    startTime: '09:00',
    endTime: '13:00',
    note: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && initialSlot) {
      setForm({
        date: initialSlot.date,
        startTime: initialSlot.startTime,
        endTime: initialSlot.endTime,
        note: ''
      });
    } else if (open) {
      const today = new Date().toISOString().slice(0, 10);
      setForm({ date: today, startTime: '09:00', endTime: '13:00', note: '' });
    }
    setError('');
  }, [open, initialSlot]);

  const calculateDuration = () => {
    if (!form.startTime || !form.endTime) return 0;
    const [sh, sm] = form.startTime.split(':').map(Number);
    const [eh, em] = form.endTime.split(':').map(Number);
    return ((eh * 60 + em) - (sh * 60 + sm)) / 60;
  };

  const duration = calculateDuration();
  const projectedTotal = (currentStats?.totalApprovedHours || 0) + (currentStats?.totalPendingHours || 0) + duration;
  const monthlyLimit = currentStats?.monthlyLimitHours || 8;
  const willExceedLimit = projectedTotal > monthlyLimit;

  const handleSubmit = async () => {
    setError('');
    if (!form.date) { setError('Date is required'); return; }
    if (duration <= 0) { setError('End time must be after start time'); return; }
    if (duration > 4) { setError('Maximum 4 hours per request'); return; }

    setSubmitting(true);
    try {
      await shortAbsenceService.create({
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime,
        note: form.note || null
      });
      onCreated?.(form.date);
    } catch (e) {
      setError(handleApiError(e) || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Short Absence Request</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {willExceedLimit && (
            <Alert severity="warning">
              This request will exceed your monthly limit of {monthlyLimit} hours.
              You can still submit, but it will be flagged for review.
            </Alert>
          )}

          <TextField
            label="Date"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Start Time"
              type="time"
              value={form.startTime}
              onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1800 }}
              fullWidth
            />
            <TextField
              label="End Time"
              type="time"
              value={form.endTime}
              onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1800 }}
              fullWidth
            />
          </Stack>

          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              Duration: <strong>{duration.toFixed(1)} hours</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Monthly total after this request: {projectedTotal.toFixed(1)} / {monthlyLimit} hours
            </Typography>
          </Box>

          <TextField
            label="Note (optional)"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || duration <= 0}
        >
          {submitting ? 'Submitting...' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

